import * as ko from 'knockout';
import {IOverride} from '../../../index';
import {PLEASE_WAIT_MESSAGE} from '../../../constants/Language';
import {IOverrideDetailsRow} from '../../override/IOverrideDetailsRow';
import {nsGroupMemberOverride} from '../../group/members/nsGroupMemberOverride';
import PromiseRequest from '../../../req/PromiseRequest';
import canMutateMembershipOverride = nsGroupMemberOverride.canMutateMembershipOverride;
import isMembershipOverrideDeleted = nsGroupMemberOverride.isMembershipOverrideDeleted;

/**
 * Expanded override panel host for a permission row (comment edit + audit fields).
 * Keeps IOverrideDetailsRow out of the main PermissionRow surface.
 */
export class PermissionOverrideDetails implements IOverrideDetailsRow {
    readonly isEditingOverrideComment = ko.observable(false);
    readonly isSavingOverrideDetails = ko.observable(false);
    readonly overrideCommentDraft = ko.observable('');
    readonly overridePanelRevision = ko.observable(0);
    readonly overrideDetailsWaitMessage = PLEASE_WAIT_MESSAGE;
    /** Sticky-note on the status pill; flipped when comment is saved. */
    readonly hasComment: KnockoutObservable<boolean>;

    private readonly getOverrideFn: () => IOverride | null;
    private readonly getSectionHeadingKeyFn: () => string | null;
    private readonly reqUpdateOverrideComment = new PromiseRequest('updateoverridecomment');

    constructor(
        getOverride: () => IOverride | null,
        getSectionHeadingKey: () => string | null = () => null,
    ) {
        this.getOverrideFn = getOverride;
        this.getSectionHeadingKeyFn = getSectionHeadingKey;
        this.hasComment = ko.observable(!!getOverride()?.comment?.trim());
    }

    getOverride(): IOverride | null {
        return this.getOverrideFn();
    }

    /** Same CMS key as the status pill (pending/synced / Modified). */
    getOverrideSectionHeadingKey(): string | null {
        return this.getSectionHeadingKeyFn();
    }

    overrideIdText(): string {
        const id = this.getOverride()?.id;
        return id == null ? '' : String(id);
    }

    overrideValueText(): string {
        return this.getOverride()?.value ?? '';
    }

    overrideSyncImportFileIdText(): string {
        const id = this.getOverride()?.syncronizedimportfileid;
        return id ? String(id) : '';
    }

    overrideCommentText(): string {
        return this.getOverride()?.comment ?? '';
    }

    overrideOverriddenWhen(): string {
        return this.getOverride()?.overriddenwhen ?? '';
    }

    overrideOverriddenByName(): string {
        return this.getOverride()?.overriddenby ?? '';
    }

    overrideDeletedWhen(): string {
        return this.getOverride()?.deletedwhen ?? '';
    }

    overrideDeletedByName(): string {
        return this.getOverride()?.deletedby ?? '';
    }

    isOverrideDeleted(): boolean {
        const override = this.getOverride();
        return !!override && isMembershipOverrideDeleted(override);
    }

    canEditOverrideComment(): boolean {
        const override = this.getOverride();
        return !!override && canMutateMembershipOverride(override);
    }

    startEditOverrideComment = (): void => {
        this.overrideCommentDraft(this.overrideCommentText());
        this.isEditingOverrideComment(true);
    };

    cancelEditOverrideComment = (): void => {
        this.isEditingOverrideComment(false);
        this.overrideCommentDraft(this.overrideCommentText());
    };

    submitOverrideComment = (): void => {
        const override = this.getOverride();
        if (!override?.id) {
            return;
        }
        this.isEditingOverrideComment(false);
        this.isSavingOverrideDetails(true);
        void this.reqUpdateOverrideComment
            .request({id: override.id, comment: this.overrideCommentDraft()})
            .then(() => {
                override.comment = this.overrideCommentDraft();
                this.hasComment(!!override.comment.trim());
                this.bumpOverridePanelRevision();
            })
            .finally(() => this.isSavingOverrideDetails(false));
    };

    /** Notify the expanded panel after this override snapshot changes. */
    bumpOverridePanelRevision(): void {
        this.overridePanelRevision(this.overridePanelRevision() + 1);
    }

    dispose(): void {
        this.reqUpdateOverrideComment.abort();
    }
}
